import { useEmployeeUser } from './EmployeeUserContext';
import { useNavigate } from 'react-router-dom';
import { Building2, CheckCircle2, Clock, XCircle, Briefcase } from 'lucide-react';

const statusConfig = {
  active: { label: 'פעיל', icon: CheckCircle2, bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', dot: 'bg-green-500' },
  pending_approval: { label: 'ממתין לאישור', icon: Clock, bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' },
  terminated: { label: 'הסתיים', icon: XCircle, bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-500', dot: 'bg-gray-400' },
} as const;

export function EmployeeEmploymentsPage() {
  const { session } = useEmployeeUser();
  const navigate = useNavigate();

  if (!session) return null;

  const { employments } = session;
  const active = employments.filter(e => e.status === 'active');
  const pending = employments.filter(e => e.status === 'pending_approval');
  const terminated = employments.filter(e => e.status === 'terminated');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-2xl bg-teal-100 flex items-center justify-center">
          <Building2 className="w-7 h-7 text-teal-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">המעסיקים שלי</h1>
          <p className="text-sm text-gray-500">{employments.length} חברות</p>
        </div>
      </div>

      {pending.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-bold text-amber-700 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            ממתינים לאישור שלך ({pending.length})
          </h2>
          {pending.map(emp => (
            <EmploymentCard key={emp.id} emp={emp}
              onApprove={() => navigate('/employee/portal/approvals')} />
          ))}
        </section>
      )}

      {active.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-bold text-green-700 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            פעילים ({active.length})
          </h2>
          {active.map(emp => <EmploymentCard key={emp.id} emp={emp} />)}
        </section>
      )}

      {terminated.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-bold text-gray-500 flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            הסתיימו ({terminated.length})
          </h2>
          {terminated.map(emp => <EmploymentCard key={emp.id} emp={emp} />)}
        </section>
      )}

      {employments.length === 0 && (
        <div className="text-center py-16">
          <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-sm">אין העסקות פעילות</p>
          <p className="text-gray-400 text-xs mt-1">ברגע שחברה תזמין אותך — ההזמנה תופיע כאן</p>
        </div>
      )}

      <div className="h-4" />
    </div>
  );
}

function EmploymentCard({ emp, onApprove }: {
  emp: { id: string; companyName: string; status: string; jobTitle: string | null; startDate: string | null; invitedAt: string | null };
  onApprove?: () => void;
}) {
  const cfg = statusConfig[emp.status as keyof typeof statusConfig] ?? statusConfig.terminated;
  const StatusIcon = cfg.icon;

  return (
    <div className={`rounded-2xl border ${cfg.border} ${cfg.bg} p-4 space-y-2`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm">{emp.companyName || 'חברה'}</p>
            {emp.jobTitle && <p className="text-xs text-gray-500">{emp.jobTitle}</p>}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.text} ${cfg.bg} border ${cfg.border}`}>
          <StatusIcon className="w-3 h-3" />
          {cfg.label}
        </span>
      </div>

      <div className="flex items-center gap-4 text-xs text-gray-500">
        {emp.startDate && <span>תחילת העסקה: {emp.startDate}</span>}
        {emp.invitedAt && <span>הוזמן: {new Date(emp.invitedAt).toLocaleDateString('he-IL')}</span>}
      </div>

      {emp.status === 'pending_approval' && onApprove && (
        <button onClick={onApprove}
          className="w-full mt-2 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700
                     text-white font-semibold text-sm transition-colors">
          אשר הצטרפות
        </button>
      )}
    </div>
  );
}
